import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash, Star, Bank, TrendUp } from "@phosphor-icons/react";

const FIELDS = [["bank_name", "Bank name"], ["branch", "Branch"], ["account_type", "Account type"], ["account_no", "Account number"], ["iban", "IBAN"], ["swift", "SWIFT code"]];
const CURRENCIES = ["USD", "EUR", "AED"];
const blank = (currency) => ({ id: "", label: `${currency} account`, currency, bank_name: "", branch: "", account_type: `${currency} - Current Account`, account_no: "", iban: "", swift: "", is_default: false });

export function BankAccountsCard() {
  const [accounts, setAccounts] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/bank-accounts").then((r) => setAccounts(r.data)).catch(() => toast.error("Could not load bank accounts")); }, []);
  const upd = (i, k, v) => setAccounts((xs) => xs.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  const setDefault = (i) => setAccounts((xs) => xs.map((a, j) => ({ ...a, is_default: j === i })));
  const remove = (i) => setAccounts((xs) => { const r = xs.filter((_, j) => j !== i); if (r.length && !r.some((a) => a.is_default)) r[0].is_default = true; return r; });
  const add = () => { const used = new Set(accounts.map((a) => a.currency)); const c = CURRENCIES.find((x) => !used.has(x)) || "USD"; setAccounts((xs) => [...xs, blank(c)]); };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/settings/bank-accounts", { accounts }); setAccounts(data); toast.success("Bank accounts saved — new invoices can use any of them"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };
  if (!accounts) return <div className="py-10 text-center text-muted-foreground">Loading…</div>;
  return (
    <div className="hp-card p-6 space-y-5" data-testid="bank-accounts-form">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-semibold text-lg">Bank Accounts</h2>
          <p className="text-sm text-muted-foreground">One account per currency (USD, EUR, AED). The default is pre-selected when issuing an invoice; you can pick another account per invoice.</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={add} data-testid="bank-account-add"><Plus size={14} className="mr-1" /> Add account</Button>
      </div>
      <div className="space-y-4">
        {accounts.map((a, i) => (
          <div key={a.id || i} className={`rounded-sm border p-4 space-y-3 ${a.is_default ? "border-primary/50 bg-primary/5" : "border-border"}`} data-testid={`bank-account-${i}`}>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-28">
                <Label className="text-xs uppercase tracking-wide">Currency</Label>
                <select className="mt-1.5 w-full h-10 rounded-sm border border-input bg-background px-2 text-sm" value={a.currency} onChange={(e) => upd(i, "currency", e.target.value)} data-testid={`bank-account-currency-${i}`}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <Label className="text-xs uppercase tracking-wide">Label</Label>
                <Input className="mt-1.5 rounded-sm" value={a.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`bank-account-label-${i}`} />
              </div>
              <Button type="button" variant={a.is_default ? "default" : "outline"} size="sm" className="rounded-sm" onClick={() => setDefault(i)} data-testid={`bank-account-default-${i}`}>
                <Star size={14} weight={a.is_default ? "fill" : "regular"} className="mr-1" /> {a.is_default ? "Default" : "Make default"}
              </Button>
              <Button type="button" variant="ghost" size="sm" className="rounded-sm text-destructive" disabled={accounts.length === 1} onClick={() => remove(i)} aria-label="Remove account" data-testid={`bank-account-remove-${i}`}><Trash size={16} /></Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-wide">{label}</Label>
                  <Input className="mt-1.5 rounded-sm" value={a[k] || ""} onChange={(e) => upd(i, k, e.target.value)} data-testid={`bank-account-${k}-${i}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Button className="rounded-sm" onClick={save} disabled={busy} data-testid="bank-accounts-save"><Bank size={16} className="mr-2" /> {busy ? "Saving…" : "Save bank accounts"}</Button>
    </div>
  );
}

export function BankAccountPicker({ currency, setCurrency, accountId, setAccountId, accounts, rate, setRate, baseTotal }) {
  const list = accounts || [];
  const forCur = list.filter((a) => a.currency === currency);
  const shown = forCur.length ? forCur : list;
  const showFx = setRate && currency !== "USD";
  const [mkt, setMkt] = useState(null);
  const [mktBusy, setMktBusy] = useState(false);
  useEffect(() => {
    if (!showFx) return;
    setMkt(null);
    api.get(`/fx/rate?to=${currency}`).then((r) => setMkt(r.data)).catch(() => setMkt({ error: true }));
  }, [currency, showFx]);
  const useMarket = async () => {
    setMktBusy(true);
    try { const { data } = mkt && !mkt.error ? { data: mkt } : await api.get(`/fx/rate?to=${currency}`); setMkt(data); setRate(String(data.rate)); toast.success(`Market rate applied: 1 USD = ${data.rate} ${currency}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Market rate unavailable — enter it manually"); }
    finally { setMktBusy(false); }
  };
  return (
    <div className="space-y-3">
    <div className="grid sm:grid-cols-2 gap-3" data-testid="bank-account-picker">
      <div>
        <Label className="text-xs uppercase tracking-wide">Invoice currency</Label>
        <select className="mt-1.5 w-full h-10 rounded-sm border border-input bg-background px-2 text-sm" value={currency} onChange={(e) => { const c = e.target.value; setCurrency(c); const m = list.find((a) => a.currency === c); if (m) setAccountId(m.id); }} data-testid="invoice-currency">
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide">Bank account on invoice</Label>
        <select className="mt-1.5 w-full h-10 rounded-sm border border-input bg-background px-2 text-sm" value={accountId || ""} onChange={(e) => setAccountId(e.target.value)} data-testid="invoice-bank-account">
          {shown.map((a) => <option key={a.id} value={a.id}>{a.label} · {a.bank_name}{a.is_default ? " (default)" : ""}</option>)}
        </select>
      </div>
    </div>
    {showFx && (
      <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="fx-box">
        <Label className="text-xs uppercase tracking-wide">Exchange rate · 1 USD = ? {currency}</Label>
        <div className="flex gap-2">
          <Input type="number" min="0" step="0.0001" className="rounded-sm bg-white flex-1" placeholder={mkt && !mkt.error ? `Market: ${mkt.rate}` : currency === "AED" ? "e.g. 3.6725" : "e.g. 0.92"} value={rate} onChange={(e) => setRate(e.target.value)} data-testid="invoice-fx-rate" />
          <Button type="button" variant="outline" size="sm" className="rounded-sm h-10 shrink-0 bg-white" onClick={useMarket} disabled={mktBusy} data-testid="use-market-rate"><TrendUp size={14} className="mr-1" /> {mktBusy ? "Fetching…" : "Use market rate"}</Button>
        </div>
        {mkt && !mkt.error && <p className="text-[11px] text-muted-foreground" data-testid="market-rate-info">Market rate today: 1 USD = {mkt.rate} {currency}{mkt.as_of ? ` · ${new Date(mkt.as_of).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}{mkt.stale ? " (cached)" : ""} · {mkt.source}</p>}
        {mkt?.error && <p className="text-[11px] text-amber-700">Market rate not available right now — enter the rate manually.</p>}
        <p className="text-xs text-muted-foreground">
          {Number(rate) > 0
            ? <>Order amounts will be converted: <b>USD {Number(baseTotal || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</b> → <b data-testid="fx-preview">{currency} {(Number(baseTotal || 0) * Number(rate)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b> (before tax).</>
            : <>Leave empty to keep the USD figures and only label the invoice in {currency}.</>}
        </p>
      </div>
    )}
    </div>
  );
}
